import React from 'react';
import { Button, DialogTitle, DialogContent, DialogActions,
  DialogProps, DialogContentText, Typography } from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';

/*
  Simple confirmation dialog that can be used to confirm or cancel an action.

  Usage Example:
    <ConfirmationDialog
      isOpen={clearDialogOpen}
      title={i18n.t("dashboard.clear-widgets")}
      description={i18n.t("dashboard.clear-widgets-description")}
      onClose={handleClearWidgets}
    ></ConfirmationDialog>

    See ConfirmationDialogRawProps below for additional options.
*/


export interface ConfirmationDialogRawProps extends Omit<DialogProps, ('onClose' | 'open')> {
  // Is the confirmation dialog open?
  isOpen: boolean;

  // Required callback to handle the user's action.
  // Should be a function that is passed a boolean indicating whether
  // the user confirmed (true) or cancelled (false) the dialog
  onClose: (confirmed: boolean, event?:any) => void;

  // The title of the dialog
  title: string;

  // Optional descriptive text to display below the title.
  // Alternately, you can pass children to display custom JSX
  description?: string;

  // Optional custom confirmation button text
  confirmText?: string;

  // Optional custom cancellation button text
  cancelText?: string;

  // Optionally hide the cancel button
  hideCancel?: boolean;

  additionalActionButtons?: React.ReactNode;
}

export default function ConfirmationDialog(props: ConfirmationDialogRawProps) {
  const { isOpen, onClose, maxWidth, title, description, confirmText, cancelText, ...otherProps } = props;

  const handleCancel = () => {
    props.onClose(false);
  };

  const handleConfirm = (event) => {
    props.onClose(true, event);
  };

  return (
    <ResponsiveDialog
      open={props.isOpen}
      onClose={handleCancel}
      maxWidth={maxWidth || "sm"}
      {...otherProps}
    >
      <DialogTitle sx={{ minWidth: "400px" }}>
        <Typography variant="h3">
          {props.title}
        </Typography>
      </DialogTitle>
      { (props.description || props.children) &&
        <DialogContent>
            <DialogContentText>
              {props.description}
            </DialogContentText>
            {props.children}
        </DialogContent>
      }
      <DialogActions sx={{ padding: "0 18px 18px 0" }} >
        {!props.hideCancel &&
          <Button
            autoFocus
            size="small"
            variant="contained"
            color="secondary"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              handleCancel();
            }} >
            { cancelText || "Cancel" }
          </Button>
        }
        <Button
          variant="contained"
          color='success'
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            handleConfirm(event);
          }} >
          { confirmText || "Ok" }
        </Button>
        {props.additionalActionButtons}
      </DialogActions>
    </ResponsiveDialog>
  );
}
